import { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from 'shared/api';
import { ButtonCustom } from 'shared/ui/button-custom';
import { DashboardHeaderBackLink } from 'widgets/dashboard-header-back-link';
import styles from './catalog-admin.module.scss';

export const CatalogAdmin = () => {
  const navigate = useNavigate();

  const [vehiclesList, setVehiclesList] = useState<any[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<any[]>([]);

  // Состояния для поисковых фильтров
  const [searchFilters, setSearchFilters] = useState({
    model: '',
    brand: '',
    type: '',
    subtype: '',
  });

  // Состояния для множественного выбора
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Функция фильтрации данных
  const filterVehicles = (vehicles: any[], filters: typeof searchFilters) => {
    return vehicles.filter((vehicle) => {
      const modelMatch =
        !filters.model || vehicle.name?.toLowerCase().includes(filters.model.toLowerCase());

      const brandMatch =
        !filters.brand ||
        vehicle.car_brand?.name?.toLowerCase().includes(filters.brand.toLowerCase());

      const typeMatch =
        !filters.type || vehicle.car_type?.name?.toLowerCase().includes(filters.type.toLowerCase());

      const subtypeMatch =
        !filters.subtype ||
        vehicle.car_subtype?.name?.toLowerCase().includes(filters.subtype.toLowerCase());

      return modelMatch && brandMatch && typeMatch && subtypeMatch;
    });
  };

  // Обработчик изменения поисковых фильтров
  const handleFilterChange = (field: keyof typeof searchFilters, value: string) => {
    const newFilters = { ...searchFilters, [field]: value };
    setSearchFilters(newFilters);

    // Применяем фильтры к текущему списку
    const filtered = filterVehicles(vehiclesList, newFilters);
    setFilteredVehicles(filtered);
  };

  // Функции для работы с выбором
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredVehicles.map((vehicle) => vehicle.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllSelected =
    filteredVehicles.length > 0 && filteredVehicles.every((vehicle) => selectedIds.has(vehicle.id));

  const isIndeterminate = selectedIds.size > 0 && !isAllSelected;

  const getVehicles = async () => {
    try {
      const response = await apiClient.get('/vehicles/all');
      const data = Array.isArray(response.data.data) ? response.data.data : [];

      const sorted = data.sort((a: any, b: any) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });

      setVehiclesList(sorted);
      // Инициализируем отфильтрованный список полным списком
      setFilteredVehicles(sorted);
    } catch (error) {
      throw new Error('Ошибка при загрузке данных');
    }
  };

  useEffect(() => {
    getVehicles();
  }, []);

  const handleVehicleClick = (vehicle: any) => {
    navigate(`/dashboard/admin/catalog/${vehicle.id}`);
  };

  const handleCreateNew = () => {
    navigate('/dashboard/admin/catalog/new');
  };

  const handleImportExcel = () => {
    navigate('/dashboard/admin/import-excel');
  };

  const handleBulkDelete = () => {
    if (selectedIds.size > 0) {
      setShowDeleteDialog(true);
    }
  };

  const performBulkDelete = async () => {
    try {
      // Удаляем записи по одной (можно оптимизировать с bulk API если есть)
      const deletePromises = Array.from(selectedIds).map((id) =>
        apiClient.delete(`/vehicles/item/${id}`),
      );

      await Promise.all(deletePromises);

      // Обновляем список после удаления
      await getVehicles();
      setSelectedIds(new Set());
      setShowDeleteDialog(false);
    } catch (error) {
      throw new Error('Ошибка при удалении записей');
    }
  };

  return (
    <Box className={styles.agentsPage}>
      <DashboardHeaderBackLink link={'/dashboard/admin/last'} title={'Список автомобилей'} />
      <Box>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
          }}
        >
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedIds.size > 0 && (
              <>
                <Typography variant='body2' color='text.secondary'>
                  Выбрано: {selectedIds.size}
                </Typography>
                <ButtonCustom onClick={handleBulkDelete} className={styles.redBtn}>
                  Удалить выбранные
                </ButtonCustom>
              </>
            )}
          </Box>
          <Box style={{ display: 'flex', gap: '20px' }}>
            <ButtonCustom onClick={handleCreateNew} className={styles.greenBtn}>
              Создать новую запись
            </ButtonCustom>
            <ButtonCustom onClick={handleImportExcel} className={styles.blueBtn}>
              Импорт из Excel
            </ButtonCustom>
          </Box>
        </Box>
        <TableContainer component={Box} className={styles.tableContainer}>
          <Table className={styles.table} sx={{ minWidth: 1070 }} aria-label={'Список автомобилей'}>
            <TableHead className={styles.tableHead}>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                >
                  {/* Пустая ячейка для колонки с чекбоксами */}
                </TableCell>
                <TableCell className={`${styles.tableHeadCell}`}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Поиск по модели...'
                    value={searchFilters.model}
                    onChange={(e) => handleFilterChange('model', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Поиск по бренду...'
                    value={searchFilters.brand}
                    onChange={(e) => handleFilterChange('brand', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Поиск по типу...'
                    value={searchFilters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Поиск по подтипу...'
                    value={searchFilters.subtype}
                    onChange={(e) => handleFilterChange('subtype', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        fontSize: '14px',
                      },
                      '& .MuiInputBase-input': {
                        padding: '8px 12px',
                      },
                    }}
                    fullWidth
                  />
                </TableCell>
              </TableRow>
              <TableRow className={styles.tableHeadRow}>
                <TableCell
                  className={`${styles.tableHeadCellFirst} ${styles.tableHeadCell}`}
                  style={{ width: '60px' }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    color='primary'
                  />
                </TableCell>
                <TableCell className={`${styles.tableHeadCell}`}>Модель</TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Бренд
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Тип
                </TableCell>
                <TableCell
                  className={`${styles.tableHeadCell} ${styles.tableCellMobile}`}
                  align='left'
                >
                  Подтип
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={styles.tableBody}>
              {filteredVehicles.map((vehicle) => (
                <TableRow
                  key={vehicle.id}
                  className={styles.tableBodyRow}
                  sx={{
                    '& td, & th': { border: 0 },
                    marginTop: '10px',
                  }}
                >
                  <TableCell
                    className={`${styles.tableBodyCellFirst} ${styles.tableBodyCell}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedIds.has(vehicle.id)}
                      onChange={(e) => handleSelectOne(vehicle.id, e.target.checked)}
                      color='primary'
                    />
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell}`}
                    component='th'
                    scope='row'
                    onClick={() => handleVehicleClick(vehicle)}
                    style={{ cursor: 'pointer' }}
                  >
                    {vehicle.name}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                    align='left'
                    onClick={() => handleVehicleClick(vehicle)}
                    style={{ cursor: 'pointer' }}
                  >
                    {vehicle.car_brand?.name || '-'}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                    align='left'
                    onClick={() => handleVehicleClick(vehicle)}
                    style={{ cursor: 'pointer' }}
                  >
                    {vehicle.car_type?.name || '-'}
                  </TableCell>
                  <TableCell
                    className={`${styles.tableBodyCell} ${styles.tableCellMobile}`}
                    align='left'
                    onClick={() => handleVehicleClick(vehicle)}
                    style={{ cursor: 'pointer' }}
                  >
                    {vehicle.car_subtype?.name || '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        aria-labelledby='bulk-delete-dialog-title'
        aria-describedby='bulk-delete-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '400px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='bulk-delete-dialog-title'>
          <span>Подтверждение удаления</span>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить {selectedIds.size}{' '}
            {selectedIds.size === 1 ? 'запись' : selectedIds.size < 5 ? 'записи' : 'записей'}? Это
            действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          <ButtonCustom
            className={styles.modalButtonCancel}
            onClick={() => setShowDeleteDialog(false)}
          >
            Отмена
          </ButtonCustom>
          <ButtonCustom className={styles.redBtn} onClick={performBulkDelete}>
            Удалить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
